import { useEffect, useRef, useState } from 'react';
import { useToken } from '@/common/helper';
import {
  getTestRunWorkflow,
  getWorkflowVersionSnapshot,
  RunGraphUnavailableError,
  WorkflowRunKind,
} from '@/apis/logiblocksAPI';
import type { LBWorkflow } from '@/proto/logiblocks/logiblocks';
import {
  getCachedRunGraphSnapshot,
  getRunGraphSourceKey,
  putCachedRunGraphSnapshot,
  type RunGraphSource,
} from './runGraphSnapshotCache';

/** Fetches the graph `source` names: a published version, or a test run's stored graph. */
function fetchRunGraph(
  token: string,
  workflowId: string,
  source: RunGraphSource
): Promise<LBWorkflow> {
  switch (source.kind) {
    case WorkflowRunKind.LIVE:
      return getWorkflowVersionSnapshot(
        token,
        workflowId,
        source.versionNumber
      );
    case WorkflowRunKind.TEST:
      return getTestRunWorkflow(token, workflowId, source.runId);
  }
}

/**
 * Where the fetch of a run's graph stands: 'idle' when no source is selected,
 * 'loading' while it is being fetched, 'loaded' once available, 'load-failed'
 * when the fetch itself failed and retrying may succeed, and 'unavailable' when
 * the graph definitively cannot be shown (it does not exist, or its bytes cannot
 * be decoded).
 */
export type RunGraphSnapshotStatus =
  'idle' | 'loading' | 'loaded' | 'load-failed' | 'unavailable';

/**
 * Loads the graph a run executed, from its published version or, for a test
 * run, from the graph stored with it. A graph is fetched once and reused from a
 * bounded cache on later selections, and a response for a source switched away
 * from is discarded. The status lets the caller show a notice for a graph whose
 * bytes could not be loaded rather than an empty canvas. Callers may pass a new
 * `source` object on every render; only a change of version or test run refetches.
 */
export function useRunGraphSnapshot(
  workflowId: string,
  source: RunGraphSource | null
): { snapshot: LBWorkflow | null; status: RunGraphSnapshotStatus } {
  const { getToken } = useToken();
  const [snapshot, setSnapshot] = useState<LBWorkflow | null>(null);
  const [status, setStatus] = useState<RunGraphSnapshotStatus>('idle');
  const requestedKey = useRef<string | null>(null);
  // The effect depends on `sourceKey`, a string, and reads `source` from this ref.
  // Depending on `source` itself would refetch for every new object with the same contents.
  const latestSource = useRef(source);
  latestSource.current = source;
  const sourceKey =
    source === null ? null : getRunGraphSourceKey(workflowId, source);

  useEffect(() => {
    // Captures the source this fetch is for. A later render may point
    // `latestSource` at another source before the response arrives.
    const currentSource = latestSource.current;
    requestedKey.current = sourceKey;

    if (currentSource === null) {
      setSnapshot(null);
      setStatus('idle');
      return;
    }

    const cached = getCachedRunGraphSnapshot(workflowId, currentSource);
    if (cached !== undefined) {
      setSnapshot(cached);
      setStatus('loaded');
      return;
    }

    // No token means logged out, so the fetch below could only fail; report
    // that rather than sit on a silent empty canvas.
    const token = getToken();
    if (token === null) {
      setSnapshot(null);
      setStatus('load-failed');
      return;
    }

    // Show nothing while the snapshot loads rather than a stale graph.
    setSnapshot(null);
    setStatus('loading');
    fetchRunGraph(token, workflowId, currentSource)
      .then((fetched) => {
        // Cache even a response we have switched away from, so returning to it
        // is instant; only paint it if it is still the selected source.
        putCachedRunGraphSnapshot(workflowId, currentSource, fetched);
        if (requestedKey.current === sourceKey) {
          setSnapshot(fetched);
          setStatus('loaded');
        }
      })
      .catch((error) => {
        if (requestedKey.current === sourceKey) {
          console.error('Failed to load run graph snapshot:', error);
          setSnapshot(null);
          setStatus(
            error instanceof RunGraphUnavailableError
              ? 'unavailable'
              : 'load-failed'
          );
        }
      });
  }, [workflowId, sourceKey, getToken]);

  return { snapshot, status };
}
